import React from 'react';

import classNames from 'classnames';

import { Loader } from '@nxte-nl/loader';

import {
  FeatureToggleWrapContainer,
} from '../../containers/FeatureToggleWrapContainer/FeatureToggleWrapContainer';
import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { ApplicationSiteGroup } from '../../types/commonEnums';
import styles from './Button.module.css';

export enum ButtonFit {
  Fill = 'fill',
  Inline = 'inline',
}

export enum ButtonType {
  Button = 'button',
  Reset = 'reset',
  Submit = 'submit',
}

export enum ButtonIntent {
  linkish = 'linkish',// white button with midnight purple border
  primary = 'primary',// ruby button
  primaryCheckOutV2 = 'primaryCheckOutV2',
  // green button CB and CK with primary intent styles'
  quaternary = 'quaternary',
  quaternaryReversed = 'quaternaryReversed',
  quinary = 'quinary',// Business page V3
  secondary = 'secondary',// white button with ruby border
  secondaryWarning = 'secondaryWarning', // white button with red border
  tertiary = 'tertiary', // ruby text as button
}

export enum ButtonLoaderColor {
  linkish = 'var(--primary-button-background)',
  // ruby
  linkishAlt = 'var(--primary-button-background-alt)',
  primary = '#ffffff',
  primaryCheckOutV2 = 'var(--white)',
  // white
  quaternary = 'var(--quaternary-button-background)',
  quaternaryReversed = 'var(--white)',
  quinary = 'var(--primary-button-background)',
  secondary = 'var(--primary-button-background)',
  // ruby
  secondaryAlt = 'var(--primary-button-background-alt)',
  secondaryWarning = 'var(--secondaryWarning-button-color)', // red
  tertiary = '#0f0a1b', // midnight purple
}

export type ButtonOptionalProps = {
  className?: string;
  dataTau?: string;
  dataTracking?: string;
  disabled?: boolean;
  form?: string;
  isContentLineBreak?: boolean;
  isLink?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler;
  type?: ButtonType;
  withNormalWhiteSpace?: boolean;
  withoutHoverStyles?: boolean;
};

export type ButtonAllOptionalProps = ButtonOptionalProps & {
  children: JSX.Element | string;
  fit?: ButtonFit;
  intent?: ButtonIntent;
};

export type Props = ButtonOptionalProps & {
  children: JSX.Element | string;
  fit: ButtonFit;
  intent: ButtonIntent;
};

export const hooks = {
  useClick({
    disabled,
    loading,
    onClick,
  }: {
    disabled?: boolean;
    loading?: boolean;
    onClick?: React.MouseEventHandler;
  }) {
    return React.useCallback(
      (event: React.MouseEvent) => {
        if (onClick && !disabled && !loading) {
          onClick(event);
        }
      },
      [onClick, disabled, loading],
    );
  },
};

export const Button = React.memo((props: Props) => {
  const {
    intent,
    fit,
    children,
    disabled = false,
    loading = false,
    className,
    form,
    onClick,
    type = 'button',
    isContentLineBreak = false,
    dataTau,
    dataTracking,
    withNormalWhiteSpace,
    withoutHoverStyles,
    isLink,
  } = props;

  const handleClick = hooks.useClick({ disabled, loading, onClick });
  const { isClient } = hydrationHooks.useIsClient();

  const buttonDisabled = React.useMemo(() => disabled || loading
  || (!isClient && !isLink), [disabled, isClient, isLink, loading]);

  return (
    <FeatureToggleWrapContainer>
      {({ isGCSOrangeColor }: FeatureToggleValues) => (
        <button
          data-tau={dataTau}
          data-tracking={dataTracking}
          className={classNames(
            styles.button,
            styles[intent],
            {
              [styles.contentLineBreak]: isContentLineBreak,
              [styles.inline]: fit === ButtonFit.Inline,
              [styles.fill]: fit === ButtonFit.Fill,
              [styles.disabled]: buttonDisabled,
              [styles.withoutHoverStyles]: withoutHoverStyles,
              [styles.primaryAlt]: CONFIG.applicationSiteGroup === ApplicationSiteGroup.GCS
                && isGCSOrangeColor
                && intent === ButtonIntent.primary,
              [styles.secondaryAlt]: CONFIG.applicationSiteGroup === ApplicationSiteGroup.GCS
                && isGCSOrangeColor
                && intent === ButtonIntent.secondary,
            },
            className,
            { [styles.noWrap]: CONFIG.isHyphenation },
          )}
          onClick={handleClick}
          disabled={buttonDisabled}
          type={type}
          form={form}
        >
          {loading && (
            <div className={styles.loaderContainer}>
              <Loader loaderColor={
                CONFIG.applicationSiteGroup === ApplicationSiteGroup.GCS
                && isGCSOrangeColor
                && intent === ButtonIntent.secondary
                  ? ButtonLoaderColor.secondaryAlt
                  : CONFIG.applicationSiteGroup === ApplicationSiteGroup.GCS
                    && isGCSOrangeColor
                    && intent === ButtonIntent.linkish
                    ? ButtonLoaderColor.linkishAlt
                    : ButtonLoaderColor[intent]} />
            </div>
          )}

          <div className={classNames(
            styles.content,
            {
              [styles.contentLineBreakInner]: isContentLineBreak,
              [styles.hidden]: loading,
              [styles.noWrap]: CONFIG.isHyphenation,
              [styles.withNormalWhiteSpace]: withNormalWhiteSpace,
            },
          )}>
            {children}
          </div>
        </button>
      )}
    </FeatureToggleWrapContainer>
  );
});

Button.displayName = 'Button';
